import { type ClientSettings, ContentType, Status, AppError, AppErrorCode} from "@ca.vmedia.tv/domains";
import { useUserInfoStore } from '../UserInfo';
import { useNuxtApp } from '#app';
import type { AxiosError } from 'axios';
import { Logger } from '@/utils';
import type { ExposeInterface } from './types';

export const useUserSettingsStore = defineStore('UserSettings', (): ExposeInterface => {

    const { $domains } = useNuxtApp();

    const userInfoStore = useUserInfoStore();
    const {
        isClient
    } = storeToRefs(userInfoStore);

    const settings: Ref<ClientSettings | null> = ref(null);
    const enabledAutoPlay: Ref<boolean> = ref(true);

    const getSettings = computed((): ClientSettings | null => settings.value);
    const getEnabledAutoplay = computed((): boolean => enabledAutoPlay.value);

    /**
     * Getter to check if the Crave service is available
     * @returns {boolean} true if the service is available and false otherwise
     */
    const isCraveAvailable = computed((): boolean => {
        if (isClient.value) {
            const craveService = settings.value?.clientServices?.find(
                (service) =>
                    service.serviceContentType === ContentType.CraveTV &&
                    service.serviceStatus === Status.Enabled)
                ?? null;
            return craveService !== null;
        } else {
            return true;
        }
    });

    /**
     * Getter to check if the VOD service is available
     * @returns {boolean} true if the service is available and false otherwise
     */
    const isVodAvailable = computed((): boolean => {
        const vodService =
            settings.value?.clientServices?.find(
                (service) =>
                    (service.serviceContentType === ContentType.VOD ||
                        service.serviceContentType === ContentType.CraveTV ||
                        service.serviceContentType === ContentType.SVOD) &&
                    service.serviceStatus === Status.Enabled
            ) ?? null;
        return vodService !== null;
    });

    /**
     * Getter to check if the guide service is available
     * @returns {boolean} true if the service is available and false otherwise
     */
    const isGuideAvailable = computed((): boolean => {
        const guideService =
            settings.value?.clientServices?.find(
                (service) =>
                    (service.serviceContentType === ContentType.LiveTV ||
                        service.serviceContentType === ContentType.DVR) &&
                    service.serviceStatus === Status.Enabled
            ) ?? null;
        return guideService !== null;
    });

    const fetchSettings = async (): Promise<void> => {
        try {
            const clientSettings: ClientSettings = await $domains.clientRepository.getSettings();

            const isAnySubscriptionAvailable = !clientSettings.clientServices?.some(
                (service) =>
                    service.serviceStatus === Status.Enabled &&
                    (service.serviceContentType === ContentType.SVOD ||
                        service.serviceContentType === ContentType.VOD ||
                        service.serviceContentType === ContentType.CraveTV ||
                        service.serviceContentType === ContentType.LiveTV ||
                        service.serviceContentType === ContentType.DVR)
            );
    
            if (isAnySubscriptionAvailable) {
                throw new AppError(AppErrorCode.SETTINGS_NO_SUBSCRIPTION_ERROR, null);
            }
            settings.value = clientSettings
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
            if (error instanceof AppError) {
                throw error;
            }
        }
    };

    const setSettingsLanguage = async (lang: string): Promise<void> => {
        try {
            await $domains.clientRepository.setSettings({language: lang, clientServices: null, settings: null, streamPreference: null});
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        }
    };

    const SetEnabledAutoplay = (value: boolean): void => {
        localStorage.setItem("Player", JSON.stringify({"autoplay": value}));
        enabledAutoPlay.value = value;
    };

    const setToInitialState = (): void => {
        settings.value = null;
        enabledAutoPlay.value = true;
    };


    return { 
        getSettings,
        isCraveAvailable,
        isGuideAvailable,
        isVodAvailable,
        getEnabledAutoplay,
        fetchSettings,
        setSettingsLanguage,
        setToInitialState,
        SetEnabledAutoplay
    };
});
